import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ReactDOM from "react-dom/client";
import Nav from './components/Nav';
import Results from './routes/Results';
import Home from './routes/Home';
import Standings from './routes/Standings';
import Rules from './routes/Rules';
import Skins from './routes/Skins';
import Members from './routes/Members';
import Score from './routes/Score';
import Tournament from './routes/Tournament';
import WeeklyScores from './routes/WeeklyScores';
import MemberStats from './routes/MemberStats';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { ThemeProvider, useTheme } from './utils/ThemeContext';

function AppContent() {
  const { theme } = useTheme();

  const muiTheme = useMemo(() => createTheme({
    palette: {
      background: {
        ...(theme.muiOverride ? { default: theme.bodyBg, paper: theme.panelBg } : {}),
      },
      text: {
        ...(theme.muiOverride ? { primary: theme.bodyText, secondary: theme.bodyText } : {}),
      },
    },
    components: theme.muiOverride ? {
      MuiTableCell: {
        styleOverrides: { root: { color: theme.bodyText } },
      },
      MuiInputBase: {
        styleOverrides: { root: { color: theme.bodyText } },
      },
      MuiInputLabel: {
        styleOverrides: { root: { color: theme.bodyText } },
      },
      MuiSelect: {
        styleOverrides: { icon: { color: theme.bodyText } },
      },
    } : {},
  }), [theme]);

  const cssVars = `
    :root {
      --color-primary: ${theme.primary};
      --color-primary-light: ${theme.primaryLight};
      --color-panel-bg: ${theme.panelBg};
      --color-panel-bg-alt: ${theme.panelBgAlt};
      --color-nav-hover: ${theme.navHover};
      --color-scorecard-holes: ${theme.scorecardHoles};
      --color-scorecard-hcp: ${theme.scorecardHcp};
      --color-scorecard-par: ${theme.scorecardPar};
      --color-body-bg: ${theme.bodyBg};
      --color-body-text: ${theme.bodyText};
    }

    /* Pierce UI5 Shadow DOM — Panel and Card text color applies to all themes */
    ui5-panel::part(header-text), ui5-panel::part(header) {
      color: ${theme.bodyText};
    }
    ui5-text::part(root) {
      color: ${theme.bodyText};
    }

    ${theme.muiOverride ? `
    /* muiOverride themes: force text color inside UI5 shadow DOM */
    ui5-panel *, ui5-card * {
      color: ${theme.bodyText};
    }
    /* muiOverride themes: override UI5 and MUI backgrounds that default to white */
    :root, ui5-panel, ui5-card {
      --sapTile_Background: ${theme.panelBg};
      --sapGroup_ContentBackground: ${theme.panelBg};
      --sapBackgroundColor: ${theme.panelBg};
      --sapBaseColor: ${theme.panelBg};
      --sapList_Background: ${theme.panelBg};
      --sapList_HeaderBackground: ${theme.panelBg};
      --sapContent_ForegroundColor: ${theme.panelBg};
      --sapTextColor: ${theme.bodyText};
      --sapContent_LabelColor: ${theme.bodyText};
      --sapList_TextColor: ${theme.bodyText};
      --sapTile_TitleTextColor: ${theme.bodyText};
      --sapGroup_TitleTextColor: ${theme.bodyText};
    }
    ui5-panel::part(content) { background-color: ${theme.panelBg}; }
    ui5-card::part(root) { background-color: ${theme.panelBg}; }
    .MuiCard-root, .MuiPaper-root {
      background-color: ${theme.panelBg} !important;
      color: ${theme.bodyText} !important;
    }
    ` : ''}
  `;

  return (
    <MuiThemeProvider theme={muiTheme}>
    <style>{cssVars}</style>
    <CssBaseline />
    <Grid alignItems="center">
      <Box>
      <BrowserRouter basename={`${process.env.REACT_APP_ROUTE_BASENAME}`}>
      <Routes>
        <Route path="/" element={<Nav />}>
          <Route index element={<Home />} />
          <Route path="results" element={<Results />} />
          <Route path="standings" element={<Standings />} />
          <Route path="skins" element={<Skins />} />
          <Route path="members" element={<Members />} />
          <Route path="rules" element={<Rules />} />
          <Route path="score" element={<Score />} />
          <Route path="tournament" element={<Tournament />} />
          <Route path="weeklyscoreboard" element={<WeeklyScores />} />
          <Route path="memberstats" element={<MemberStats />} />
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
      </BrowserRouter>
    </Box>
    </Grid>
    </MuiThemeProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
